import { FormEvent, useEffect, useRef, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { multiFactor, TotpMultiFactorGenerator, TotpSecret } from 'firebase/auth';
import { getFunctions } from 'firebase/functions';
import { httpsCallable } from '../lib/callable';
import QRCode from 'qrcode';
import { AlertCircle, ShieldCheck } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { CLOUD_FUNCTIONS } from '../constants/firebase';

export default function SetupMfa() {
  const { user, loading, mfaRequired, mfaEnrolled } = useAuth();
  const navigate = useNavigate();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [secret, setSecret] = useState<TotpSecret | null>(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user || !mfaRequired || mfaEnrolled) return;
    let active = true;
    multiFactor(user).getSession()
      .then((session) => TotpMultiFactorGenerator.generateSecret(session))
      .then((nextSecret) => {
        if (!active) return;
        setSecret(nextSecret);
      })
      .catch((err) => {
        console.error('[SetupMfa] TotpMultiFactorGenerator.generateSecret failed:', err);
        setError('Authenticator setup is not available. Contact your administrator.');
      });
    return () => { active = false; };
  }, [user, mfaRequired, mfaEnrolled]);

  useEffect(() => {
    if (!secret || !canvasRef.current) return;
    QRCode.toCanvas(
      canvasRef.current,
      secret.generateQrCodeUrl(user?.email || user?.uid || '', 'DocumentSpark'),
      { width: 220, margin: 1 }
    ).catch((err) => {
      console.error('[SetupMfa] QRCode.toCanvas failed:', err);
    });
  }, [secret, user]);

  if (loading) return <div className="flex min-h-screen items-center justify-center">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (!mfaRequired || mfaEnrolled) return <Navigate to="/" replace />;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!secret) return;
    setSaving(true); setError('');
    try {
      const assertion = TotpMultiFactorGenerator.assertionForEnrollment(secret, code.trim());
      await multiFactor(user).enroll(assertion, 'Authenticator app');
      await httpsCallable(getFunctions(), CLOUD_FUNCTIONS.COMPLETE_MFA_ENROLLMENT)({});
      navigate('/', { replace: true });
    } catch {
      setError('That verification code is invalid or expired. Try the latest code from your app.');
    } finally { setSaving(false); }
  };

  return <main className="flex min-h-screen items-center justify-center bg-[#F7F9FB] px-6 py-12" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
    <section className="w-full max-w-xl rounded-3xl border border-[#DCE2E5] bg-white p-8 shadow-xl sm:p-12">
      <ShieldCheck className="mb-5 h-10 w-10 text-[var(--brand-primary)]" />
      <p className="mb-3 text-sm font-bold uppercase tracking-[0.14em] text-[var(--brand-primary)]">Account security</p>
      <h1 className="text-4xl font-normal tracking-[-0.04em] text-[#001820]">Set up your authenticator</h1>
      <p className="mt-4 text-sm leading-6 text-[#5F696C]">Scan this QR code using Google Authenticator, Microsoft Authenticator, 1Password, or another TOTP app.</p>
      {error && <div className="mt-5 flex gap-2 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700"><AlertCircle className="h-5 w-5 shrink-0" />{error}</div>}
      <div className="my-7 flex justify-center rounded-2xl border border-[#DCE2E5] bg-white p-4"><canvas ref={canvasRef} aria-label="Authenticator QR code" /></div>
      {secret && <div className="mb-6 rounded-xl bg-[#F1F6F6] p-4"><p className="text-xs font-bold uppercase tracking-wider text-[#5F696C]">Manual setup key</p><code className="mt-2 block break-all text-sm font-semibold text-[#001820]">{secret.secretKey}</code></div>}
      <form onSubmit={submit}>
        <label className="block text-sm font-bold text-[#0B1C30]">Six-digit verification code<input required inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]{6}" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} className="mt-2 h-14 w-full rounded-xl border border-[#CAD2D5] px-4 text-center text-xl tracking-[0.3em] outline-none focus:border-[var(--brand-primary)] focus:ring-4 focus:ring-[var(--brand-primary)]/15" placeholder="000000" /></label>
        <button disabled={saving || !secret || code.length !== 6} className="mt-6 w-full rounded-full bg-[#001820] px-5 py-3.5 text-sm font-bold text-white hover:bg-[#00313A] disabled:opacity-50">{saving ? 'Verifying…' : 'Activate authenticator'}</button>
      </form>
    </section>
  </main>;
}
